import { NativeEventEmitter, NativeModules } from 'react-native';
import { useCallback, useEffect, useState } from 'react';

import AuthService from '../services/AuthService';
import FeedsService from '@omni/kit/services/FeedsService';
import { IUserProfileInfo } from '../services/FeedsService/GetUserProfile';
import { SUBSPLASH_AUTH_PROVIDER_ID } from '../constants/identifiers';
import { getTokenUrl } from '../services/AuthService/utilities';
import { useSourceContext } from '../contexts/SourceContext';

const debug = require('debug')('omni:kit:hooks:useUserInfo');

export default function useUserInfo(
  appKey?: string,
  accessToken?: string | null
): {
  user?: IUserProfileInfo;
  isLoading: boolean;
} {
  // Local state
  const [user, setUser] = useState<IUserProfileInfo | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const source = useSourceContext();

  const fetchUser = useCallback(async () => {
    setIsLoading(true);

    let response = await FeedsService.getUserProfile({
      appKey: appKey ?? '',
      token: accessToken ?? '',
    });

    /**
     * User may have logged-in on a third-party auth provider
     * e.g. Ligonier (JF2Q5K) Protected Content
     * When this happens a 401 will occur on user-info,
     * since it expects a subsplash token.
     * We need to do a token exchange to get a subsplash token
     *
     * TODO: PPL-1567: refactor when we port the remaining native auth behaviors
     * to react-native
     */
    if (response.status === 401 || response.status === 403) {
      const tokenUrl = await getTokenUrl(appKey, SUBSPLASH_AUTH_PROVIDER_ID);

      /**
       * Dev note: Be careful not to call AuthService.refreshAccessToken()
       * here to avoid side effects and potential infinite loop problems.
       * e.g. a Ligonier RN component and Subsplash RN component
       * are both trying to refresh the token at the same time.
       *
       * TODO: PPL-1567: refactor when we port the remaining native auth
       * behaviors to react-native including cached access tokens for each
       * auth provider. Right now we only have a shared cached token in
       * AuthService.
       */

      if (tokenUrl) {
        const tokenResponse = await AuthService.requestAccessToken(
          tokenUrl,
          source
        );

        if (tokenResponse.ok) {
          const json = await tokenResponse.json();
          const retryAccessToken = `${json.token_type} ${json.access_token}`;

          // try again
          response = await FeedsService.getUserProfile({
            appKey: appKey ?? '',
            token: retryAccessToken ?? '',
          });
        }
      }
    }

    const userInfo = response.body;

    if (userInfo) {
      let customInitials: string | undefined;

      // Enterprise clients may customize the initials, e.g. 5289PV
      if (userInfo.customProfileHeaderUrl && appKey) {
        try {
          const customProfileHeader = await FeedsService.getCustomProfileHeader(
            {
              appKey,
              url: userInfo.customProfileHeaderUrl,
            }
          );

          customInitials = customProfileHeader?.body?.subtitle;
        } catch (error) {
          debug(error);
        }
      }

      const adjustedUserInfo: IUserProfileInfo = {
        ...userInfo,
        ...(customInitials && { initials: customInitials }),
      };

      setUser(adjustedUserInfo);
    }

    setIsLoading(false);
  }, [appKey, accessToken]);

  useEffect(() => {
    if (appKey && accessToken) {
      fetchUser();
    } else {
      setUser(undefined);
      setIsLoading(false);
    }
  }, [accessToken, appKey, fetchUser]);

  useEffect(() => {
    const { ReactPlatformBridge } = NativeModules;

    if (!ReactPlatformBridge) return;

    const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
      ReactPlatformBridge
    );

    const listener = ReactPlatformBridgeEventEmitter.addListener(
      'NotifyAppUserSettingsUpdated',
      () => {
        fetchUser(); // refresh avatar and initials if it was changed
      }
    );

    return () => {
      listener?.remove();
    };
  }, [fetchUser]);

  return { user, isLoading };
}
